import type { DependsOnType } from '@unifyapps/defs/types/page';
import { useGetQueryMeta } from '@unifyapps/network/hooks/useGetQueryMeta';
import getDomain from '@unifyapps/network/utils/url/getDomain';
import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import isEqual from 'react-fast-compare';
import { EMPTY_OBJECT } from '../../../consts/empty';
import { useAppDependencyGraphProvider } from '../../context/DependencyGraphProvider';
import { useGetAppSubscribeToDependencyUpdates } from '../../context/DependencyGraphProvider/hooks/useGetSubscribeToDependencyUpdates';
import {
  useGetGlobalAppStateStoreState,
  useGlobalAppStateStore,
} from '../../stores/app/GlobalAppStateStore';
import InterfaceHelper from '../../stores/InterfaceStore/helper/InterfaceHelper';
import {
  useGetWalkthroughComputeContext,
  useWalkthroughComputeContext,
} from '../../stores/WalkthroughManagerStore/hooks';
import type { ComputeContextMode } from './const';
import ContextBuilder from './context/ContextBuilder';
import ContextFactory from './context/ContextFactory';
import type { ComputeContextType } from './context/types';
import { useGetAppInterfaceEntityMethods } from './useGetInterfaceEntityMethods';
import { useGetNoCodeComputedData } from './useGetNoCodeComputedData';
import { useAppInterfaceEntityMethods } from './useInterfaceEntityMethods';
import { getInterfaceRecordForComputeContext } from './utils/getInterfaceRecordForComputeContext';
import { getIsDependentContextChanged } from './utils/getIsDependentContextChanged';

export type GetAppComputeContextBuilder = () => ComputeContextType;

/**
 * App-level compute context builder hook
 */
function useAppComputeContextBuilder({ mode }: { mode: ComputeContextMode }) {
  const { getGlobalAppStateStoreState } = useGetGlobalAppStateStoreState();
  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();

  const appComputeContextBuilderRef = useRef<GetAppComputeContextBuilder | null>(null);
  const getComputeContextForMethods = useCallback(
    () => appComputeContextBuilderRef.current?.() ?? {},
    [],
  );

  const {
    getActionsForAppComputeContext,
    getDataSourceMethodsForAppComputeContext,
    getLocalStorageMethodsForAppComputeContext,
    getPageVariableMethodsForAppComputeContext,
    getSessionStorageMethodsForAppComputeContext,
    getPageFunctionMethodsForAppComputeContext,
  } = useGetAppInterfaceEntityMethods({ mode, getComputeContext: getComputeContextForMethods });
  const { getWalkthroughComputeContext } = useGetWalkthroughComputeContext();
  const contextBuilder = useMemo(() => new ContextBuilder(), []);
  const queryMetaContext = useGetQueryMeta();
  const queryMeta = useMemo(() => {
    const basePath =
      queryMetaContext?.basePath || InterfaceHelper.getInterfaceBasePath(interfaceRecord);
    return { domain: getDomain(queryMetaContext), basePath };
  }, [queryMetaContext, interfaceRecord]);

  const getAppComputeContextBuilder = useCallback<GetAppComputeContextBuilder>(() => {
    const appGlobalState = getGlobalAppStateStoreState();
    const walkthrough = getWalkthroughComputeContext();
    const interfaceRecordForContext = getInterfaceRecordForComputeContext({
      appGlobalState,
    });

    return ContextFactory.createAppContextBuilder(
      {
        appState: appGlobalState,
        interfaceRecord: interfaceRecordForContext,
        dataSourceMethods: getDataSourceMethodsForAppComputeContext(),
        localStorageMethods: getLocalStorageMethodsForAppComputeContext(),
        sessionStorageMethods: getSessionStorageMethodsForAppComputeContext(),
        pageVariableMethods: getPageVariableMethodsForAppComputeContext(),
        pageFunctionMethods: getPageFunctionMethodsForAppComputeContext(),
        actions: getActionsForAppComputeContext(),
        queryMeta,
        walkthrough,
      },
      contextBuilder,
    ).build();
  }, [
    getGlobalAppStateStoreState,
    getActionsForAppComputeContext,
    getDataSourceMethodsForAppComputeContext,
    getLocalStorageMethodsForAppComputeContext,
    getSessionStorageMethodsForAppComputeContext,
    getPageVariableMethodsForAppComputeContext,
    getPageFunctionMethodsForAppComputeContext,
    queryMeta,
    getWalkthroughComputeContext,
    contextBuilder,
  ]);

  appComputeContextBuilderRef.current = getAppComputeContextBuilder;

  return {
    getAppComputeContextBuilder,
  };
}

export function useGetAppComputeContext({ mode = 'readonly' }: { mode?: ComputeContextMode } = {}) {
  const { getAppComputeContextBuilder } = useAppComputeContextBuilder({ mode });

  return { getAppComputeContext: getAppComputeContextBuilder };
}

export function useReactiveAppComputeContext({
  mode = 'readonly',
}: {
  mode?: ComputeContextMode;
} = {}) {
  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();
  const location = useGlobalAppStateStore().hooks.useLocation();
  const userContext = useGlobalAppStateStore().hooks.useUserContext();
  const deviceContext = useGlobalAppStateStore().hooks.useDeviceContext();
  const appsAndIntegrations = useGlobalAppStateStore().hooks.useAppsAndIntegrations();
  const envVariables = useGlobalAppStateStore().hooks.useEnvVariables();
  const dataSources = useGlobalAppStateStore().hooks.useDataSources();
  const dataSourcesMeta = useGlobalAppStateStore().hooks.useDataSourcesMeta();
  const localStorage = useGlobalAppStateStore().hooks.useLocalStorage();
  const sessionStorage = useGlobalAppStateStore().hooks.useSessionStorage();
  const pageVariables = useGlobalAppStateStore().hooks.usePageVariables();
  const pageFunctions = useGlobalAppStateStore().hooks.usePageFunctions();
  const colorSchemeDetails = useGlobalAppStateStore().hooks.useColorSchemeDetails();
  const navigation = useGlobalAppStateStore().hooks.useNavigation();
  const deviceDetails = useGlobalAppStateStore().hooks.useDeviceDetails();
  const appMode = useGlobalAppStateStore().hooks.useMode();
  const permissions = useGlobalAppStateStore().hooks.usePermissions();
  const activeAppVariantKey = useGlobalAppStateStore().hooks.useActiveAppVariantKey();
  const queryMetaContext = useGetQueryMeta();
  const { walkthroughContext } = useWalkthroughComputeContext();
  const contextBuilder = useMemo(() => new ContextBuilder(), []);

  const basePath =
    queryMetaContext?.basePath || InterfaceHelper.getInterfaceBasePath(interfaceRecord);

  const queryMeta = useMemo(
    () => ({ domain: getDomain(queryMetaContext), basePath }),
    [queryMetaContext, basePath],
  );

  const reactiveAppContextRef = useRef<ComputeContextType | null>(null);
  const getComputeContextForMethods = useCallback(() => reactiveAppContextRef.current ?? {}, []);

  const {
    actions,
    dataSourceMethods,
    localStorageMethods,
    sessionStorageMethods,
    pageVariableMethods,
    pageFunctionMethods,
  } = useAppInterfaceEntityMethods({ mode, getComputeContext: getComputeContextForMethods });

  const appGlobalStateForInterfaceContext = useMemo(
    () => ({
      interfaceRecord,
      activeAppVariantKey,
      deviceDetails,
      mode: appMode,
      permissions,
      navigation,
      location,
      basePath,
    }),
    [
      interfaceRecord,
      activeAppVariantKey,
      deviceDetails,
      appMode,
      permissions,
      navigation,
      location,
      basePath,
    ],
  );

  const interfaceRecordForContext = useMemo(
    () =>
      getInterfaceRecordForComputeContext({
        appGlobalState: appGlobalStateForInterfaceContext,
      }),
    [appGlobalStateForInterfaceContext],
  );

  const context = useMemo<ComputeContextType>(() => {
    const appState = {
      envVariables,
      location,
      userContext,
      deviceContext,
      appsAndIntegrations,
      dataSources,
      dataSourcesMeta,
      localStorage,
      sessionStorage,
      pageVariables,
      pageFunctions,
      colorSchemeDetails,
      navigation,
    };

    return ContextFactory.createAppContextBuilder(
      {
        appState,
        interfaceRecord: interfaceRecordForContext,
        dataSourceMethods,
        localStorageMethods,
        sessionStorageMethods,
        pageVariableMethods,
        pageFunctionMethods,
        actions,
        queryMeta,
        walkthrough: walkthroughContext,
      },
      contextBuilder,
    ).build();
  }, [
    actions,
    location,
    userContext,
    deviceContext,
    appsAndIntegrations,
    dataSourceMethods,
    envVariables,
    dataSources,
    dataSourcesMeta,
    localStorageMethods,
    sessionStorageMethods,
    localStorage,
    sessionStorage,
    pageVariables,
    pageFunctions,
    pageVariableMethods,
    pageFunctionMethods,
    queryMeta,
    colorSchemeDetails,
    contextBuilder,
    walkthroughContext,
    navigation,
    interfaceRecordForContext,
  ]);

  reactiveAppContextRef.current = context;

  return { context };
}

// entityId you want to subscribe to for app-level context
export function useNonReactiveAppComputeContext({
  entityId,
  key,
  mode = 'readonly',
}: {
  entityId: string | null;
  key?: string;
  mode?: ComputeContextMode;
}) {
  const serverSnapshotRef = useRef<{
    context?: Record<string, unknown>;
    dependsOn?: DependsOnType[];
  }>(EMPTY_OBJECT);
  const snapshotRef = useRef<{
    context?: Record<string, unknown>;
    dependsOn?: DependsOnType[];
  }>(EMPTY_OBJECT);

  const { getAppComputeContextBuilder } = useAppComputeContextBuilder({
    mode,
  });

  const { graphManager, subscriptionManager } = useAppDependencyGraphProvider();
  const { subscribeToDependencyUpdates } = useGetAppSubscribeToDependencyUpdates();
  const { getNoCodeBulkComputedData } = useGetNoCodeComputedData();

  const getSnapshot = useCallback(() => {
    let lastDependsOn = snapshotRef.current.dependsOn;
    let prevContext = snapshotRef.current.context;

    const dependsOn = graphManager.getNode(entityId)?.dependsOn;

    // if no dependsOn, then no need to subscribe
    if (!dependsOn?.length) return;

    // first check if the dependency item is changed, then rerender
    if (!isEqual(dependsOn, lastDependsOn)) {
      lastDependsOn = dependsOn;
    }

    const latestContext = getAppComputeContextBuilder();

    const { isContextChanged, nextContext } = getIsDependentContextChanged({
      prevContext,
      context: latestContext,
      dependsOn,
      notifyingUpdateForId: subscriptionManager.getNotifyingUpdateForId(),
      getNoCodeBulkComputedData,
    });

    if (isContextChanged) {
      prevContext = nextContext;
    }

    const shouldReRender =
      lastDependsOn !== snapshotRef.current.dependsOn ||
      prevContext !== snapshotRef.current.context;

    if (shouldReRender) {
      snapshotRef.current = {
        context: prevContext,
        dependsOn,
      };
    }

    return snapshotRef.current;
  }, [
    graphManager,
    entityId,
    getAppComputeContextBuilder,
    subscriptionManager,
    getNoCodeBulkComputedData,
  ]);

  const subscribe = useCallback(
    (callback: () => void) => {
      if (!entityId) {
        return () => {
          // noop
        };
      }

      // subscribe to dependency updates for the given entityIds
      const unsubscribe = subscribeToDependencyUpdates({
        id: entityId,
        callback,
        key,
      });

      return () => {
        unsubscribe();
      };
    },
    [entityId, key, subscribeToDependencyUpdates],
  );

  const getServerSnapshot = useCallback(() => {
    if (!entityId) {
      return;
    }

    const dependsOn = graphManager.getNode(entityId)?.dependsOn;

    if (!dependsOn?.length) {
      return;
    }

    const latestContext = getAppComputeContextBuilder();

    const { isContextChanged, nextContext } = getIsDependentContextChanged({
      prevContext: undefined, // no previous context on the server
      context: latestContext,
      dependsOn,
      notifyingUpdateForId: subscriptionManager.getNotifyingUpdateForId(),
      getNoCodeBulkComputedData,
    });

    const shouldReRender =
      dependsOn !== serverSnapshotRef.current.dependsOn ||
      nextContext !== serverSnapshotRef.current.context;

    if (isContextChanged && shouldReRender) {
      serverSnapshotRef.current = {
        context: nextContext,
        dependsOn,
      };
    }

    return serverSnapshotRef.current;
  }, [
    entityId,
    graphManager,
    getAppComputeContextBuilder,
    subscriptionManager,
    getNoCodeBulkComputedData,
  ]);

  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  return { context: getAppComputeContextBuilder() };
}
